export const ARCHITECT_HOME = '/architect';

// Mirror of backend LANDING_PAGE_PERMISSION (backend/permissions.py). The backend already
// resolves landing_page on login/me; this mirror only stops the client from navigating to a
// page the user cannot open. Backend permissions stay authoritative.
export const LANDING_PAGE_PERMISSION = {
  '/dashboard': 'dashboard.view',
  '/architect': 'architect.dashboard.view',
  '/finance': 'finance.view',
  '/leads': 'leads.view',
  '/import': 'import.manage',
  '/projects': 'leads.view',
  '/clients': 'clients.view',
  '/work-queue': 'leads.view',
  '/calendar': 'calendar.view',
  '/my-calls': 'call.view_own',
  '/b2b-partners': 'b2b.view',
  '/targets': 'targets.view',
  '/performance': 'reports.view',
  '/settings': 'settings.view',
};

export const LANDING_PAGE_LABELS = {
  '/dashboard': 'Dashboard',
  '/architect': 'Architect Workspace',
  '/finance': 'Finance',
  '/leads': 'Leads',
  '/import': 'Bulk Import',
  '/projects': 'All Projects',
  '/clients': 'Clients',
  '/work-queue': "Today's Work",
  '/calendar': 'Calendar',
  '/my-calls': 'My Calls',
  '/b2b-partners': 'B2B Partners',
  '/targets': 'Sales Targets',
  '/performance': 'Performance',
  '/settings': 'Settings',
};

const LANDING_FALLBACK_ORDER = [
  '/dashboard', '/architect', '/finance', '/leads', '/import', '/projects', '/clients',
  '/work-queue', '/calendar', '/my-calls', '/b2b-partners', '/targets', '/performance', '/settings',
];

// Sales Manager is NOT an architect role: permissions.py grants it no architect.* permission and
// the backend denies architect.dashboard.view, so it can never land on the Architect workspace.
const ARCHITECT_LANDING_ROLES = new Set(['architect', 'architect_manager', 'super_admin']);

// Mirror of backend ROLE_KEY_ALIASES. Used ONLY to decide which dashboard component to
// render, so a legacy/variant role key (e.g. a "Sales Manager" role created from the UI
// before the locked-role guard existed) can never fall through to the generic dashboard.
// Permissions are never derived from this map — the backend stays authoritative.
export const ROLE_KEY_ALIASES = {
  sales_manager: 'manager',
  seals_manager: 'manager',
  sale_manager: 'manager',
  salesmanager: 'manager',
  sales_mgr: 'manager',
  manager_sales: 'manager',
  architect_mgr: 'architect_manager',
  architecture_manager: 'architect_manager',
  finance_mgr: 'finance_manager',
  accounts_manager: 'finance_manager',
  sales_exec: 'sales_executive',
  executive: 'sales_executive',
  superadmin: 'super_admin',
  admin: 'super_admin',
};

export const canonicalRole = (role) => {
  const key = String(role || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
  return ROLE_KEY_ALIASES[key] || key;
};

export const isArchitectUser = (user) => user?.role === 'architect' || user?.role === 'architect_manager';

const canLandOn = (user, path) => {
  const required = LANDING_PAGE_PERMISSION[path];
  if (!required) return false;
  if (path === ARCHITECT_HOME && !ARCHITECT_LANDING_ROLES.has(user?.role)) return false;
  if (user?.role === 'super_admin') return true;
  // permissions not loaded yet -> rely on the route guard rather than mis-redirecting
  if (!Array.isArray(user?.permissions)) return true;
  return user.permissions.includes(required);
};

export const getSafeLandingPage = (user) => {
  if (isArchitectUser(user)) return ARCHITECT_HOME;
  if (canLandOn(user, user?.landing_page)) return user.landing_page;
  return LANDING_FALLBACK_ORDER.find((p) => canLandOn(user, p)) || '/unauthorized';
};

export const shouldRedirectArchitect = (user, pathname) => {
  if (!isArchitectUser(user)) return false;
  return pathname !== ARCHITECT_HOME && !pathname.startsWith(`${ARCHITECT_HOME}/`);
};
